import { Button, EmojiPicker, Separator, StringSelect } from "@tellescope/ui";
import { Editor } from "@tiptap/react";
import {
  Bold,
  Code,
  Code2,
  Eye,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Minus,
  PanelRight,
  Pencil,
  Quote,
  Redo2,
  Strikethrough,
  Underline,
  Undo2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { ImageButton } from "./ImageButton";
import { LinkButton } from "./LinkButton";
import { MarkButton } from "./MarkButton";

const FONT_SIZE_OPTIONS = ["12", "14", "16", "18", "20", "24", "28", "32"].map((size) => ({
  value: `${size}px`,
  label: size,
}));

export interface EditorToolbarProps {
  editor: Editor | null;
  /** Formatting controls (marks, lists, font size) — shown only when rich text is enabled. */
  showFormatting: boolean;
  /** Emoji picker — shown on the Text tab (both rich and plaintext). */
  showEmoji: boolean;
  /** In HTML mode, the toolbar hosts the Edit / Preview toggle. */
  htmlMode: boolean;
  showPreview: boolean;
  onShowPreview: (showPreview: boolean) => void;
  paletteOpen: boolean;
  onTogglePalette: () => void;
  /** Routes an emoji to the active surface. Defaults to inserting into the TipTap document. */
  onInsertEmoji?: (emoji: string) => void;
}

/**
 * The editor toolbar, present in every mode. Formatting appears only on the rich Text surface; emoji
 * appears on the whole Text tab; the token palette toggle is always available (right-aligned) so the
 * token selector is reachable from Text and HTML alike.
 */
export const EditorToolbar = ({
  editor,
  showFormatting,
  showEmoji,
  htmlMode,
  showPreview,
  onShowPreview,
  paletteOpen,
  onTogglePalette,
  onInsertEmoji,
}: EditorToolbarProps) => {
  const { t } = useTranslation("templates");
  if (!editor) return null;

  const insertEmoji = onInsertEmoji ?? ((emoji: string) => editor.chain().focus().insertContent(emoji).run());

  return (
    // Two regions: the formatting controls wrap on the left (flex-1 + min-w-0), while the Template
    // fields toggle stays pinned top-right (shrink-0) and is never pushed off-screen by wrapping.
    <div className="flex items-start gap-1 border-b bg-gray-50 p-1">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
        {htmlMode && (
          <>
            <Button
              type="button"
              size="sm"
              variant={showPreview ? "ghost" : "secondary"}
              className="h-8 gap-1.5"
              onClick={() => onShowPreview(false)}
            >
              <Pencil className="size-4" />
              {t("Edit")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={showPreview ? "secondary" : "ghost"}
              className="h-8 gap-1.5"
              onClick={() => onShowPreview(true)}
            >
              <Eye className="size-4" />
              {t("Preview")}
            </Button>
          </>
        )}
        {showFormatting && (
          <>
            <MarkButton
              label={t("Bold")}
              icon={<Bold />}
              isActive={editor.isActive("bold")}
              onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <MarkButton
              label={t("Italic")}
              icon={<Italic />}
              isActive={editor.isActive("italic")}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <MarkButton
              label={t("Underline")}
              icon={<Underline />}
              isActive={editor.isActive("underline")}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <MarkButton
              label={t("Strikethrough")}
              icon={<Strikethrough />}
              isActive={editor.isActive("strike")}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            />
            <MarkButton
              label={t("Inline code")}
              icon={<Code />}
              isActive={editor.isActive("code")}
              onClick={() => editor.chain().focus().toggleCode().run()}
            />
            <Separator
              orientation="vertical"
              className="mx-1 h-4 self-center bg-gray-300 data-[orientation=vertical]:h-4"
            />
            <MarkButton
              label={t("Heading 1")}
              icon={<Heading1 />}
              isActive={editor.isActive("heading", { level: 1 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            />
            <MarkButton
              label={t("Heading 2")}
              icon={<Heading2 />}
              isActive={editor.isActive("heading", { level: 2 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            />
            <MarkButton
              label={t("Heading 3")}
              icon={<Heading3 />}
              isActive={editor.isActive("heading", { level: 3 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            />
            <StringSelect
              options={FONT_SIZE_OPTIONS}
              onValueChange={(size) => editor.chain().focus().setFontSize(size).run()}
              placeholder={t("Size")}
              className="ml-1 h-8 w-24"
            />
            <Separator
              orientation="vertical"
              className="mx-1 h-4 self-center bg-gray-300 data-[orientation=vertical]:h-4"
            />
            <MarkButton
              label={t("Bulleted list")}
              icon={<List />}
              isActive={editor.isActive("bulletList")}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            />
            <MarkButton
              label={t("Numbered list")}
              icon={<ListOrdered />}
              isActive={editor.isActive("orderedList")}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            />
            <MarkButton
              label={t("Quote")}
              icon={<Quote />}
              isActive={editor.isActive("blockquote")}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
            />
            <MarkButton
              label={t("Code block")}
              icon={<Code2 />}
              isActive={editor.isActive("codeBlock")}
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            />
            <Separator
              orientation="vertical"
              className="mx-1 h-4 self-center bg-gray-300 data-[orientation=vertical]:h-4"
            />
            <LinkButton editor={editor} />
            <ImageButton editor={editor} />
            <Separator
              orientation="vertical"
              className="mx-1 h-4 self-center bg-gray-300 data-[orientation=vertical]:h-4"
            />
            {showEmoji && <EmojiPicker onEmojiSelect={insertEmoji} />}
            <Separator
              orientation="vertical"
              className="mx-1 h-4 self-center bg-gray-300 data-[orientation=vertical]:h-4"
            />
            <MarkButton
              label={t("Horizontal line")}
              icon={<Minus />}
              isActive={false}
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
            />
            <Separator
              orientation="vertical"
              className="mx-1 h-4 self-center bg-gray-300 data-[orientation=vertical]:h-4"
            />
            <MarkButton
              label={t("Undo")}
              icon={<Undo2 />}
              isActive={false}
              disabled={!editor.can().undo()}
              onClick={() => editor.chain().focus().undo().run()}
            />
            <MarkButton
              label={t("Redo")}
              icon={<Redo2 />}
              isActive={false}
              disabled={!editor.can().redo()}
              onClick={() => editor.chain().focus().redo().run()}
            />
          </>
        )}
        {showEmoji && !showFormatting && <EmojiPicker onEmojiSelect={insertEmoji} />}
      </div>
      <Button
        type="button"
        size="sm"
        variant={paletteOpen ? "secondary" : "ghost"}
        aria-pressed={paletteOpen}
        className="h-8 shrink-0 gap-1.5"
        onClick={onTogglePalette}
      >
        <PanelRight className="size-4" />
        {t("Template fields")}
      </Button>
    </div>
  );
};
